import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { ListPagination } from '@/components/efs/ListPagination';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { listMaster } from '@/lib/api-masters';
import { fetchInboundLots } from '@/features/operations/inbound/api';
import { fetchOutbounds } from '@/features/operations/outbounds/api';
import {
  QueryFailureNotice,
  AsyncStateBoundary,
  AuditIssueCard,
  AuditIssueMessage,
  LocalizedApiError,
  presentationForException,
} from '@/features/audit-issues';

import {
  useCreateException,
  useExceptions,
  useResolveException,
  useWaiveException,
} from './useExceptions';
import {
  buildCreateSchema,
  buildResolveSchema,
  decisionValues,
  exceptionSeverityValues,
  exceptionStatusValues,
  isDecisionRequired,
  reportableExceptionCodes,
  type CreateExceptionInput,
  type ExceptionReportDto,
  type ResolveInput,
} from './types';

const NS = 'exceptions';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100] as const;

type ActionKind = 'resolve' | 'waive';

const ActionDialog = ({
  open,
  onOpenChange,
  kind,
  exception,
  showTechnicalDetails,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  kind: ActionKind;
  exception: ExceptionReportDto | null;
  showTechnicalDetails: boolean;
}) => {
  const { t } = useTranslation([NS]);
  const resolve = useResolveException();
  const waive = useWaiveException();
  const mut = kind === 'resolve' ? resolve : waive;

  // FR-007: quality/packaging differences cannot be closed without an
  // explicit decision — mirror the backend rule in the form schema.
  const requireDecision = !!exception && isDecisionRequired(exception.exception_code);

  const form = useForm<ResolveInput>({
    resolver: zodResolver(buildResolveSchema(t, requireDecision)),
    defaultValues: { resolution_note: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (!exception) return;
    // Failure stays inside the dialog as a localized alert; the form and
    // the retry path are preserved (spec-13 §9).
    await mut.mutateAsync({ exceptionId: exception.exception_id, body: values }).then(
      () => {
        form.reset();
        onOpenChange(false);
      },
      () => undefined,
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kind === 'resolve'
              ? t('efs.exceptions.drawer.resolve_title')
              : t('efs.exceptions.drawer.waive_title')}
          </DialogTitle>
          <DialogDescription asChild>
            <div>
              {exception ? (
                <AuditIssueMessage presentation={presentationForException(exception)} />
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="resolution_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.drawer.resolution_label')}</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="decision"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.exceptions.drawer.decision_label')}
                    {requireDecision ? ' *' : ''}
                  </FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('efs.exceptions.drawer.decision_placeholder')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {decisionValues.map((d) => (
                        <SelectItem key={d} value={d}>
                          {t(`efs.exceptions.decisions.${d}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="decision_note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.drawer.decision_note_label')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {mut.isError ? (
              <LocalizedApiError error={mut.error} showTechnicalDetails={showTechnicalDetails} />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mut.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={mut.isPending}>
                {mut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.exceptions.drawer.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

/** Target types the report picker can offer records for (spec-13 §8.1). */
const PICKER_TARGET_TYPES = [
  'monthly_batch',
  'inbound_lot',
  'outbound',
  'incoming_item',
  'material',
  'destination',
] as const;
type PickerTargetType = (typeof PICKER_TARGET_TYPES)[number];

interface PickerOption {
  id: string;
  label: string;
}

/**
 * Real destinations-master row shape (see DestinationsListPage): keyed by
 * `destination_company_id`, displayed via `company_display_name` with a
 * `japanese_name` fallback. Exported for regression testing (round-2 §11).
 */
export interface DestinationMasterRow {
  destination_company_id: string;
  company_display_name?: string | null;
  japanese_name?: string | null;
}

export const destinationPickerOption = (row: DestinationMasterRow): PickerOption | null => {
  const label = row.company_display_name ?? row.japanese_name ?? '';
  if (!row.destination_company_id || !label) return null;
  return { id: row.destination_company_id, label };
};

/** Load authorized record options (business codes/names, never UUIDs). */
const usePickerOptions = (batchId: string, type: PickerTargetType) =>
  useQuery<PickerOption[]>({
    queryKey: ['exceptions', 'target-picker', batchId, type],
    enabled: type !== 'monthly_batch',
    queryFn: async () => {
      switch (type) {
        case 'inbound_lot': {
          const { data } = await fetchInboundLots(batchId, { per_page: 200 });
          return data.map((lot) => ({ id: lot.inbound_lot_id, label: lot.lot_code }));
        }
        case 'outbound': {
          const { data } = await fetchOutbounds(batchId, { per_page: 200 });
          return data.map((o) => ({
            id: o.outbound_id,
            label: o.outbound_code ?? o.planned_dispatch_date,
          }));
        }
        case 'incoming_item': {
          const { rows } = await listMaster<{ incoming_item_id: string; japanese_name: string }>(
            'incoming-items',
            { per_page: 200 },
          );
          return rows.map((r) => ({ id: r.incoming_item_id, label: r.japanese_name }));
        }
        case 'material': {
          const { rows } = await listMaster<{ material_id: string; japanese_name: string }>(
            'materials',
            { per_page: 200 },
          );
          return rows.map((r) => ({ id: r.material_id, label: r.japanese_name }));
        }
        case 'destination': {
          const { rows } = await listMaster<DestinationMasterRow>('destinations', {
            per_page: 200,
          });
          return rows.map(destinationPickerOption).filter((o): o is PickerOption => o !== null);
        }
        default:
          return [];
      }
    },
  });

/** FR-007 difference-report entry (batch-scoped pages only). */
const CreateReportDialog = ({
  open,
  onOpenChange,
  batchId,
  showTechnicalDetails,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  batchId: string;
  showTechnicalDetails: boolean;
}) => {
  const { t } = useTranslation([NS, 'common', 'issues']);
  const create = useCreateException();
  const [targetType, setTargetType] = useState<PickerTargetType>('monthly_batch');
  const options = usePickerOptions(batchId, targetType);

  const form = useForm<CreateExceptionInput>({
    resolver: zodResolver(buildCreateSchema(t)),
    defaultValues: {
      exception_code: 'quality_difference',
      severity: 'warning',
      description: '',
      cause: '',
      affects_quotation: false,
      additional_cost: false,
      // FR-008 binding: the batch is the coarsest target; the picker below
      // narrows it to a concrete business record (spec-13 §8.1).
      target_record_type: 'monthly_batch',
      target_record_id: batchId,
    },
  });

  const changeTargetType = (value: string) => {
    const next = (PICKER_TARGET_TYPES as readonly string[]).includes(value)
      ? (value as PickerTargetType)
      : 'monthly_batch';
    setTargetType(next);
    form.setValue('target_record_type', next);
    form.setValue('target_record_id', next === 'monthly_batch' ? batchId : '');
  };

  const onSubmit = form.handleSubmit(async (values) => {
    await create.mutateAsync({ batchId, body: values }).then(
      () => {
        form.reset();
        setTargetType('monthly_batch');
        onOpenChange(false);
      },
      () => undefined,
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.exceptions.report.title')}</DialogTitle>
          <DialogDescription>{t('efs.exceptions.report.hint')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <FormField
              control={form.control}
              name="exception_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.fields.exception_code')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {reportableExceptionCodes.map((c) => (
                        <SelectItem key={c} value={c}>
                          {t(`efs.issues.codes.${c}.title`, { ns: 'issues' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="severity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.fields.severity')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {exceptionSeverityValues.map((s) => (
                        <SelectItem key={s} value={s}>
                          {t(`efs.exceptions.severities.${s}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Target picker over authorized business records (spec-13 §8.1). */}
            <div className="grid gap-3 sm:grid-cols-2">
              <FormItem>
                <FormLabel>{t('efs.exceptions.report.target_type_label')}</FormLabel>
                <Select value={targetType} onValueChange={changeTargetType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PICKER_TARGET_TYPES.map((ty) => (
                      <SelectItem key={ty} value={ty}>
                        {t(`efs.issues.record_types.${ty}`, { ns: 'issues' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
              {targetType !== 'monthly_batch' ? (
                <FormField
                  control={form.control}
                  name="target_record_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('efs.exceptions.report.target_record_label')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                options.isLoading
                                  ? t('efs.issues.states.loading', { ns: 'issues' })
                                  : t('efs.exceptions.report.target_record_placeholder')
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(options.data ?? []).map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {options.isError ? (
                        // Round-6 item 5: retry + request id; the form keeps
                        // the current type/target selection across the retry.
                        <QueryFailureNotice
                          compact
                          error={options.error}
                          onRetry={() => void options.refetch()}
                        />
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <p className="self-end pb-2 text-xs text-efs-text-muted">
                  {t('efs.exceptions.report.target_batch_hint')}
                </p>
              )}
            </div>
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.fields.description')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cause"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.exceptions.report.cause_label')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-6">
              <FormField
                control={form.control}
                name="affects_quotation"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      {t('efs.exceptions.report.affects_quotation')}
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="additional_cost"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      {t('efs.exceptions.report.additional_cost')}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
            {create.isError ? (
              <LocalizedApiError error={create.error} showTechnicalDetails={showTechnicalDetails} />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.exceptions.report.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const ExceptionsPage = () => {
  const { t } = useTranslation([NS, 'common', 'issues']);
  const user = useCurrentUser();
  const { batchId } = useParams<{ batchId: string }>();
  // Filters live in the URL so they survive navigating to a target and back
  // (spec-13 §8.1).
  const [searchParams, setSearchParams] = useSearchParams();

  const severity = searchParams.get('severity') ?? '';
  const status = searchParams.get('status') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const perPage = Number(searchParams.get('perPage') ?? '') === 100 ? 100 : DEFAULT_PER_PAGE;
  const highlightId = searchParams.get('exceptionId');

  const patchParams = (patch: Record<string, string | null>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const list = useExceptions({
    severity: severity || undefined,
    status: status || undefined,
    monthly_batch_id: batchId,
    page,
    per_page: perPage,
  });

  const [dialog, setDialog] = useState<{ kind: ActionKind; ex: ExceptionReportDto } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const canResolve = hasPermission(user, 'exception.manage');
  const canWaive = hasPermission(user, 'exception.manage');
  // Report entry needs a batch context — only offered on the batch-scoped page.
  const canReport = !!batchId && hasPermission(user, 'exception.report.create');
  // Raw legacy descriptions and record UUIDs are system diagnostics. Report
  // or resolution authority is not technical-disclosure authority.
  const showTechnicalDetails = hasPermission(user, 'system.admin');

  const total = list.data?.meta?.total ?? 0;

  // Scroll the deep-linked exception into view once its row is rendered.
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const rowsKey = list.data?.data?.length ?? 0;
  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center' });
    }
  }, [highlightId, rowsKey]);

  const statusLabel = useMemo(
    () => (e: ExceptionReportDto) => t(`efs.exceptions.statuses.${e.status}`),
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.exceptions.title')}
        subtitle={t('efs.exceptions.subtitle')}
        count={total}
      />

      <div className="flex flex-wrap items-center gap-3">
        {canReport ? (
          <Button onClick={() => setCreateOpen(true)}>{t('efs.exceptions.report.open')}</Button>
        ) : null}
        <Select
          value={severity}
          onValueChange={(v) => patchParams({ severity: v === 'all' ? null : v, page: null })}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t('efs.exceptions.filters.all_severities')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('efs.exceptions.filters.all_severities')}</SelectItem>
            {exceptionSeverityValues.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`efs.exceptions.severities.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => patchParams({ status: v === 'all' ? null : v, page: null })}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder={t('efs.exceptions.filters.all_statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('efs.exceptions.filters.all_statuses')}</SelectItem>
            {exceptionStatusValues.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`efs.exceptions.statuses.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AsyncStateBoundary
        isLoading={list.isLoading}
        isError={list.isError}
        error={list.error}
        isFetching={list.isFetching && !list.isLoading}
        data={list.data}
        isEmpty={(d) => (d.data ?? []).length === 0}
        onRetry={() => void list.refetch()}
        emptyContent={
          <p className="text-sm text-efs-text-muted">{t('efs.exceptions.messages.no_open')}</p>
        }
      >
        {(d) => (
          <div className="space-y-2">
            {(d.data ?? []).map((e) => {
              const presentation = presentationForException(e);
              const highlighted = highlightId === e.exception_id;
              return (
                <div key={e.exception_id} ref={highlighted ? highlightRef : undefined}>
                  <AuditIssueCard
                    presentation={presentation}
                    statusLabel={statusLabel(e)}
                    showTechnicalDetails={showTechnicalDetails}
                    className={highlighted ? 'ring-2 ring-efs-accent' : undefined}
                    extraContent={
                      <>
                        {e.supplier_reply ? (
                          <p className="text-xs">
                            <span className="rounded bg-efs-surface-hover px-1.5 py-0.5">
                              {t(`efs.exceptions.supplier_replies.${e.supplier_reply}`)}
                            </span>
                            {e.supplier_reply_note ? (
                              <span className="mt-0.5 block text-efs-text-muted">
                                <span className="mr-1">
                                  {t('efs.issues.manual.note_label', { ns: 'issues' })}:
                                </span>
                                {e.supplier_reply_note}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                        {e.decision ? (
                          <p className="text-xs text-efs-text-muted">
                            {t('efs.exceptions.fields.decision')}:{' '}
                            {t(`efs.exceptions.decisions.${e.decision}`)}
                          </p>
                        ) : null}
                      </>
                    }
                    actions={
                      <>
                        {e.status === 'open' && canResolve ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDialog({ kind: 'resolve', ex: e })}
                          >
                            {t('efs.exceptions.actions.resolve')}
                          </Button>
                        ) : null}
                        {e.status === 'open' && canWaive ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDialog({ kind: 'waive', ex: e })}
                          >
                            {t('efs.exceptions.actions.waive')}
                          </Button>
                        ) : null}
                      </>
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </AsyncStateBoundary>

      <ListPagination
        page={page}
        perPage={perPage}
        total={total}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        onPageChange={(next) => patchParams({ page: String(next) })}
        onPerPageChange={(next) =>
          patchParams({ perPage: next === 100 ? '100' : null, page: null })
        }
      />

      <ActionDialog
        open={!!dialog}
        onOpenChange={(v) => {
          if (!v) setDialog(null);
        }}
        kind={dialog?.kind ?? 'resolve'}
        exception={dialog?.ex ?? null}
        showTechnicalDetails={showTechnicalDetails}
      />
      {batchId ? (
        <CreateReportDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          batchId={batchId}
          showTechnicalDetails={showTechnicalDetails}
        />
      ) : null}
    </div>
  );
};

export default ExceptionsPage;
